/**
 * OrphanCheckScreen - Handle unassigned "In Progress" issues
 * Allows assigning orphan issues to members or skipping them
 */

import { useState, useEffect, useCallback } from 'react';
import { useGame } from '../context/GameContext';
import { ApiService } from '../services/ApiService';
import { getInitials, getAvatarColor } from '../utils';
import type { SquadMember } from '../types';
import './OrphanCheckScreen.css';

export function OrphanCheckScreen() {
  const { state, actions } = useGame();
  const { orphanIssues, members } = state;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentOrphan = orphanIssues[currentIndex];
  const hasMoreOrphans = currentIndex < orphanIssues.length - 1;
  const isComplete = currentIndex >= orphanIssues.length;

  // Handle assigning issue to member
  const handleAssign = useCallback(async (member: SquadMember) => {
    if (!currentOrphan || assigning) return;

    setAssigning(true);
    setError(null);

    try {
      await ApiService.assignIssue(currentOrphan.key, member.accountId);

      // Move to next orphan or complete
      if (hasMoreOrphans) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setCurrentIndex(orphanIssues.length); // Mark complete
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign issue');
    } finally {
      setAssigning(false);
    }
  }, [currentOrphan, assigning, hasMoreOrphans, orphanIssues.length]);

  // Handle skipping issue
  const handleSkip = useCallback(() => {
    if (hasMoreOrphans) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setCurrentIndex(orphanIssues.length); // Mark complete
    }
    setError(null);
  }, [hasMoreOrphans, orphanIssues.length]);

  // When all orphans are processed, go to SPIN
  useEffect(() => {
    if (isComplete) {
      actions.setPhase('GAME_MODE_SELECT');
    }
  }, [isComplete, actions]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (assigning) return;

      if (e.code === 'Escape' || e.code === 'KeyX') {
        handleSkip();
      }
      // Number keys 1-9 to select members
      if (e.code.startsWith('Digit')) {
        const index = parseInt(e.code.replace('Digit', ''), 10) - 1;
        if (index >= 0 && index < members.length) {
          handleAssign(members[index]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [assigning, members, handleAssign, handleSkip]);

  // No orphan issues - shouldn't happen but handle it
  if (orphanIssues.length === 0) {
    return (
      <div className="screen orphan-screen">
        <h2 className="title">NO ORPHAN ISSUES</h2>
        <p className="subtitle">All issues are assigned!</p>
        <button className="pixel-button" onClick={() => actions.setPhase('GAME_MODE_SELECT')}>
          CONTINUE
        </button>
      </div>
    );
  }

  // All orphans processed
  if (isComplete) {
    return (
      <div className="screen orphan-screen">
        <div className="loading-spinner" />
        <p className="subtitle">Starting spin...</p>
      </div>
    );
  }

  return (
    <div className="screen orphan-screen">
      <div className="orphan-header">
        <h2 className="title">ORPHAN ALERT!</h2>
        <p className="orphan-count">
          Issue {currentIndex + 1} of {orphanIssues.length}
        </p>
      </div>

      {/* Console crash animation indicator */}
      <div className="crash-animation">
        <span className="crash-icon">💥</span>
        <span className="crash-text">UNASSIGNED ISSUE DETECTED</span>
      </div>

      {/* Current orphan issue */}
      {currentOrphan && (
        <div className="orphan-issue">
          <div className="issue-header">
            <span className="issue-key">{currentOrphan.key}</span>
            <span className={`issue-status status-${currentOrphan.fields.status.statusCategory.key}`}>
              {currentOrphan.fields.status.name}
            </span>
          </div>
          <p className="issue-summary">{currentOrphan.fields.summary}</p>
          {currentOrphan.fields.storyPoints && (
            <span className="issue-points">{currentOrphan.fields.storyPoints} SP</span>
          )}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="orphan-error">
          {error}
        </div>
      )}

      {/* Member selection */}
      <div className="member-selection">
        <h3 className="selection-title">ASSIGN TO:</h3>
        <div className="member-grid">
          {members.map((member, index) => (
            <button
              key={member.accountId}
              className="member-button"
              onClick={() => handleAssign(member)}
              disabled={assigning}
            >
              <span className="member-number">{index + 1}</span>
              <div
                className="member-avatar"
                style={{ backgroundColor: getAvatarColor(member.displayName) }}
              >
                {getInitials(member.displayName)}
              </div>
              <span className="member-name">{member.displayName}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Skip option */}
      <button
        className="skip-button"
        onClick={handleSkip}
        disabled={assigning}
      >
        [X] SKIP - Leave unassigned
      </button>

      {/* Controls */}
      <div className="orphan-controls">
        <span>[1-9] Select member</span>
        <span>[X] Skip</span>
      </div>

      {/* Loading overlay */}
      {assigning && (
        <div className="assigning-overlay">
          <div className="loading-spinner" />
          <p>Assigning...</p>
        </div>
      )}
    </div>
  );
}

export default OrphanCheckScreen;
